import type { ReactNode } from "react";
import { ArrowRight, Star, Sparkles } from "lucide-react";
import { LocaleLink } from "../i18n/LocaleLink";
import { Container, PostCard, PLACEHOLDER_PHOTO } from "../components/ui";
import { postsFor, POST_DATE, coverFor, type Post } from "../content/posts";
import { useLocale } from "../i18n/useLocale";
import { cmsImage } from "../i18n/cmsImage";

/** Small dark-chip + arrow "see all" style link, matching the source sections. */
function ChipLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <LocaleLink to={to} className="group inline-flex items-center gap-2.5">
      <span className="flex h-[18px] w-[18px] items-center justify-center rounded-full bg-ink text-white transition-transform group-hover:translate-x-0.5">
        <ArrowRight size={11} strokeWidth={2.4} />
      </span>
      <span className="text-[15px] font-semibold text-ink">{children}</span>
    </LocaleLink>
  );
}

function SectionGrid({ posts }: { posts: Post[] }) {
  return (
    <div className="mt-12 grid gap-5 sm:grid-cols-2 md:mt-[72px]">
      {posts.map((p) => (
        <PostCard
          key={p.slug}
          to={`/blog/${p.slug}`}
          tag={p.tag}
          title={p.title}
          read={p.read}
          date={POST_DATE[p.slug]}
          image={coverFor(p)}
        />
      ))}
    </div>
  );
}

export default function Resources() {
  const { t, locale, market } = useLocale();
  const isInternational = market === "international";

  const posts = postsFor(locale);
  const featured = posts.find((p) => p.featured) ?? posts[0];
  const rest = posts.filter((p) => p !== featured);
  const mid = Math.ceil(rest.length / 2);
  const fresh = rest.slice(0, mid);
  const more = rest.slice(mid);

  return (
    <>
      {/* Hero */}
      <section className="bg-white">
        <Container className="py-20 text-center md:py-28">
          <div className="mx-auto flex max-w-3xl flex-col items-center gap-6">
            <div className="flex flex-col items-center gap-8">
              <span className="inline-flex items-center gap-2.5 rounded-full border border-line bg-white py-1.5 pl-1.5 pr-4 shadow-sm">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-bg-subtle text-ink-soft">
                  <Sparkles size={14} strokeWidth={2} />
                </span>
                <span className="text-[14px] font-medium text-ink">{t("resources:hero.eyebrow")}</span>
              </span>
              <h1 className="max-w-[760px] font-display text-4xl font-semibold leading-[1.1] tracking-tight text-ink md:text-[48px] md:leading-[1.12]">
                {isInternational ? t("resources:hero.title.intl") : t("resources:hero.title.be")}
              </h1>
            </div>
            <p className="max-w-xl text-[16px] leading-relaxed text-ink-soft">
              {isInternational ? t("resources:hero.lead.intl") : t("resources:hero.lead.be")}
            </p>
            <div className="flex items-center gap-1">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} size={16} className="fill-line-strong text-line-strong" strokeWidth={0} />
              ))}
            </div>
          </div>
        </Container>
      </section>

      {/* Featured */}
      {featured && (
        <section className="bg-white">
          <Container className="pb-16 md:pb-24">
            <PostCard
              featured
              to={`/blog/${featured.slug}`}
              tag={featured.tag}
              title={featured.title}
              read={featured.read}
              lead={featured.lead}
              date={POST_DATE[featured.slug]}
              image={coverFor(featured)}
            />
          </Container>
        </section>
      )}

      {/* Fresh reads */}
      <section className="bg-bg">
        <Container className="pb-12 pt-20 md:pt-28">
          <div className="mx-auto flex max-w-xl flex-col items-center gap-6 text-center">
            <h2 className="font-display text-[32px] font-semibold leading-[1.1] tracking-tight text-ink md:text-[40px] md:leading-[48px]">
              {t("resources:fresh.title.line1")}
              <br />
              {t("resources:fresh.title.line2")}
            </h2>
            <p className="text-[16px] leading-relaxed text-ink-soft">
              {t("resources:fresh.lead")}
            </p>
            <ChipLink to="/trainings">{t("resources:fresh.trainingsLink")}</ChipLink>
          </div>
          <SectionGrid posts={fresh} />
        </Container>
      </section>

      {/* Handpicked */}
      {more.length > 0 && (
        <section className="bg-bg">
          <Container className="py-12">
            <div className="mx-auto flex max-w-xl flex-col items-center gap-6 text-center">
              <h2 className="font-display text-[32px] font-semibold leading-[1.1] tracking-tight text-ink md:text-[40px] md:leading-[48px]">
                {t("resources:handpicked.title.line1")}
                <br />
                {t("resources:handpicked.title.line2")}
              </h2>
              <p className="text-[16px] leading-relaxed text-ink-soft">
                {t("resources:handpicked.lead")}
              </p>
            </div>
            <SectionGrid posts={more} />
          </Container>
        </section>
      )}

      {/* Why Done-it */}
      <section className="bg-bg">
        <Container className="pb-24 pt-12 md:pb-28">
          <div className="mx-auto flex max-w-3xl flex-col items-center gap-10 text-center">
            <div className="flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-line-strong text-ink-soft">
                <Sparkles size={16} strokeWidth={2} />
              </span>
              <span className="font-display text-[28px] font-semibold tracking-tight text-ink md:text-[32px]">{t("resources:why.eyebrow")}</span>
            </div>
            <p className="font-display text-[28px] font-semibold leading-[1.25] tracking-tight text-ink/40 md:text-[40px] md:leading-[48px]">
              {t("resources:why.body")}
            </p>
            <LocaleLink to="/about" className="group inline-flex items-center gap-2.5">
              <span className="flex h-[18px] w-[18px] items-center justify-center rounded-full bg-ink text-white transition-transform group-hover:translate-x-0.5">
                <ArrowRight size={11} strokeWidth={2.4} />
              </span>
              <span className="text-[18px] font-semibold text-ink/50">{t("resources:why.cta")}</span>
            </LocaleLink>
          </div>
        </Container>
      </section>

      {/* CTA */}
      <section className="bg-white">
        <Container className="py-20 md:py-28">
          <div className="grid items-stretch gap-3 rounded-3xl border border-line bg-white p-3 shadow-[0_28px_64px_-28px_rgba(28,25,23,0.22)] md:grid-cols-2">
            <div className="relative min-h-[240px] overflow-hidden rounded-2xl bg-bg-subtle">
              <img src={cmsImage(t, "resources:hero.image", PLACEHOLDER_PHOTO)} alt="" loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover" />
            </div>
            <div className="flex flex-col justify-between gap-10 rounded-2xl bg-bg-subtle p-4 md:p-8">
              <h3 className="font-display text-[22px] font-semibold leading-[1.2] tracking-tight text-ink md:text-[24px]">
                {t("resources:cta.title")}
              </h3>
              <div className="flex flex-col gap-5">
                <p className="text-[15px] leading-relaxed text-ink-soft">
                  {t("resources:cta.body")}
                </p>
                <form className="flex gap-2.5" onSubmit={(e) => e.preventDefault()}>
                  <input
                    type="email"
                    placeholder={t("resources:cta.emailPlaceholder")}
                    className="w-full rounded-lg border border-line-strong bg-white px-4 py-3 text-[15px] text-ink outline-none placeholder:text-ink-faint focus:border-primary"
                  />
                  <button
                    type="submit"
                    className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg bg-ink px-5 py-3 font-display text-[14px] font-semibold text-white transition-colors hover:bg-ink/90"
                  >
                    {t("resources:cta.subscribe")}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </Container>
      </section>
    </>
  );
}

